import { Route, Routes, Outlet } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ProviderWrapper } from '@/shared/providers';

import ErrorBoundary from '@/shared/components/ErrorBoundary';

// Layouts
import MainLayout from './features/pages/components/layout';
import Menu from '@/shared/components/MenuBar';
import CollectionMenu from '@/features/collections/components/CollectionMenu';
import CollectionDetailLayout from '@/features/collections/components/CollectionDetailLayout';

import SharedCollections from './features/public/components/PublicCollections';
import SharedCollectionNote from './features/public/components/PublicCollectionNote';
import SharedCollectionDetail from './features/public/components/PublicCollectionDetail';
import SharedCollectionDetailLayout from '@/features/public/components/PublicCollections/CollectionDetailLayout';

// Lazy-loaded components
const HomePage = lazy(() => import('@/pages/HomePage'));
const DocumentPage = lazy(() => import('@/features/pages/components/DocumentPage'));
const SharedPage = lazy(() => import('@/pages/SharedPage'));
const PrivacyPolicy = lazy(() => import('./pages/Privacy'));
const CollectionsPage = lazy(() => import('@/features/collections/components/CollectionsPage'));
const CollectionDetailPage = lazy(
  () => import('@/features/collections/components/CollectionDetailPage')
);

// Loading component for Suspense
const PageLoader = () => null;

const queryClient = new QueryClient();

export default function App() {
  return (
    <ErrorBoundary>
      <ProviderWrapper>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />

              {/* Routes with sidebar/menu layout */}
              <Route
                path="/"
                element={
                  <MainLayout Menu={<Menu />}>
                    <Suspense fallback={<PageLoader />}>
                      <Outlet />
                    </Suspense>
                  </MainLayout>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="page/:pageId" element={<DocumentPage />} />
              </Route>

              <Route
                path="/collections"
                element={
                  <MainLayout Menu={<CollectionMenu />}>
                    <Suspense fallback={<PageLoader />}>
                      <CollectionsPage />
                    </Suspense>
                  </MainLayout>
                }
              />

              <Route
                path="/collection"
                element={
                  <CollectionDetailLayout Menu={<Menu />}>
                    <Suspense fallback={<PageLoader />}>
                      <Outlet />
                    </Suspense>
                  </CollectionDetailLayout>
                }
              >
                <Route path=":collectionId" element={<CollectionDetailPage />} />
                <Route path=":collectionId/page/:pageId" element={<CollectionDetailPage />} />
              </Route>

              {/* Shared route without sidebar/menu */}
              <Route path="/shared/:pageId" element={<SharedPage />} />
              <Route path="/shared/collections" element={<SharedCollections />} />
              <Route
                path="/shared/collections"
                element={
                  <SharedCollectionDetailLayout Menu={<Menu />}>
                    <Suspense fallback={<PageLoader />}>
                      <Outlet />
                    </Suspense>
                  </SharedCollectionDetailLayout>
                }
              >
                <Route path=":collectionId" element={<SharedCollectionDetail />} />
                <Route path=":collectionId/page/:pageId" element={<SharedCollectionNote />} />
              </Route>
            </Routes>
          </Suspense>
        </QueryClientProvider>
      </ProviderWrapper>
    </ErrorBoundary>
  );
}
